/** Shared HTTP client for stiqos-api / ShiftIQ ops endpoints. */

export const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8001";
export const SHIFTIQ_API = API_BASE;

/** Identity-issued Ops JWT. Old `pg_token` values used pg_users.id and must not be reused. */
export const OPS_TOKEN_KEY = "ops_identity_token";
const LEGACY_TOKEN_KEY = "pg_token";
export const OPS_IMPERSONATOR_TOKEN_KEY = "ops_impersonator_token";

export function getOpsToken() {
  try {
    if (typeof window === "undefined") return null;
    const current = window.localStorage.getItem(OPS_TOKEN_KEY);
    if (current) return current;
    window.localStorage.removeItem(LEGACY_TOKEN_KEY);
    return null;
  } catch {
    return null;
  }
}

export function setOpsToken(token) {
  const value = String(token || "").trim();
  if (!value) {
    clearOpsToken();
    return;
  }
  try {
    window.localStorage.setItem(OPS_TOKEN_KEY, value);
    window.localStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function clearOpsToken() {
  try {
    window.localStorage.removeItem(OPS_TOKEN_KEY);
    window.localStorage.removeItem(LEGACY_TOKEN_KEY);
    window.localStorage.removeItem(OPS_IMPERSONATOR_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function getOpsImpersonatorToken() {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(OPS_IMPERSONATOR_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setOpsImpersonatorToken(token) {
  const value = String(token || "").trim();
  try {
    if (!value) {
      window.localStorage.removeItem(OPS_IMPERSONATOR_TOKEN_KEY);
      return;
    }
    window.localStorage.setItem(OPS_IMPERSONATOR_TOKEN_KEY, value);
  } catch {
    /* ignore */
  }
}

export function clearOpsImpersonatorToken() {
  setOpsImpersonatorToken("");
}

export function getApiKey() {
  return (
    process.env.REACT_APP_STIQ_OPS_API_KEY ||
    process.env.REACT_APP_OPS_API_KEY ||
    process.env.REACT_APP_STIQOS_API_KEY ||
    null
  );
}

export function authHeaders(extra = {}) {
  const apiKey = getApiKey();
  const headers = { "Content-Type": "application/json", ...extra };
  try {
    const token = getOpsToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    /* ignore */
  }
  if (apiKey) headers["X-API-Key"] = apiKey;
  return headers;
}

/** @deprecated use authHeaders — kept for incremental App.js migration */
export function getAuthHeaders() {
  return authHeaders();
}

export function authHeadersMultipart() {
  // Must not set Content-Type — browser needs the multipart boundary.
  const headers = { ...authHeaders() };
  delete headers["Content-Type"];
  return headers;
}

/** @deprecated use authHeadersMultipart */
export function getAuthHeadersMultipart() {
  return authHeadersMultipart();
}

export function apiDetail(data, fallback) {
  const detail = data && data.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object" && typeof detail.message === "string") {
    return detail.message;
  }
  return fallback || "Request failed";
}

export async function parseJson(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(apiDetail(data, `HTTP ${res.status}`));
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export function isAbortError(err) {
  return err?.name === "AbortError";
}

/** True when the session JWT (or grants) was rejected — safe to clear local token. */
export function isAuthRejectionStatus(status) {
  return status === 401 || status === 403;
}

const AUTH_ME_ATTEMPTS = 5;
const AUTH_ME_RETRY_MS = 2000;

/**
 * Load the Ops session for a Bearer token. Rejects with ``err.status`` set on
 * HTTP errors. Callers must only ``clearOpsToken`` on auth rejection — network
 * / 5xx during an API deploy must not wipe the session.
 */
export async function fetchOpsAuthMe(token, { signal } = {}) {
  const res = await fetch(`${API_BASE}/auth/me`, {
    headers: authHeaders({ Authorization: `Bearer ${String(token || "").trim()}` }),
    signal,
  });
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/** Retry transient failures (deploy blips); rethrow 401/403 immediately. */
export async function fetchOpsAuthMeWithRetry(token, { signal, attempts = AUTH_ME_ATTEMPTS, retryMs = AUTH_ME_RETRY_MS } = {}) {
  let lastErr;
  for (let i = 0; i < attempts; i += 1) {
    try {
      return await fetchOpsAuthMe(token, { signal });
    } catch (err) {
      lastErr = err;
      if (isAbortError(err) || isAuthRejectionStatus(err?.status)) throw err;
      if (i < attempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, retryMs));
      }
    }
  }
  throw lastErr;
}

export async function apiFetch(path, options = {}) {
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const res = await fetch(url, {
    ...options,
    headers: { ...authHeaders(), ...(options.headers || {}) },
  });
  return parseJson(res);
}
